import {
  IconButton,
  Input,
  Tooltip,
  TooltipProps,
} from '@material-tailwind/react';
import classNames from 'classnames';
import clsx from 'clsx';
import { useCallback } from 'react';
import AddCircle from '$/components/icons/AddCircle';
import DoNotDisturbOn from '$/components/icons/DoNotDisturbOn';
import Info from '$/components/icons/Info';

export interface NumberSelectorProps {
  value: number;
  onChange: (value: number) => void;
  minValue?: number; // default 0
  maxValue?: number; // default 99
  step?: number; // default 1
  disabled?: boolean;
  label?: string;
  tooltip?: string;
  tooltipPlacement?: TooltipProps['placement'];
  onTooltipHover?: () => void;
  id?: string;
  className?: string;
  inputClassName?: string;
  hideInput?: boolean;
}

export default function NumberSelector({
  value,
  onChange,
  minValue = 0,
  maxValue = 99,
  step = 1,
  disabled,
  label,
  tooltip,
  tooltipPlacement = 'top',
  onTooltipHover,
  id,
  className,
  inputClassName,
  hideInput,
}: NumberSelectorProps): JSX.Element {
  const clamp = useCallback(
    (val: number) => Math.min(maxValue, Math.max(minValue, val)),
    [maxValue, minValue],
  );

  const change = useCallback(
    (delta: number) => {
      if (disabled) return;
      onChange(clamp(value + delta));
    },
    [clamp, disabled, onChange, value],
  );

  return (
    <div
      className={classNames(
        'flex flex-col gap-spacing/gap/soft-6px',
        className,
      )}
    >
      {label && (
        <label
          htmlFor={id}
          className="mhg-alpha-typography-caption inline-flex items-center text-highlights/gray-dark"
        >
          {label}
          {tooltip && (
            <Tooltip
              content={
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                <div className="max-w-[242px] md:max-w-65 lg:max-w-[286px] xl:max-w-[322px]">
                  {tooltip}
                </div>
              }
              placement={tooltipPlacement}
            >
              <span
                className="ml-1 inline-flex items-center"
                onMouseEnter={onTooltipHover}
              >
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <Info className="inline-block size-shapes/icon-size-md align-middle text-secondary-lighter" />
              </span>
            </Tooltip>
          )}
        </label>
      )}
      <div className="relative w-full">
        {!hideInput && (
          <Input
            id={id}
            disabled={disabled}
            min={minValue}
            max={maxValue}
            type="number"
            value={value}
            onChange={(e) => {
              const raw = Number(e.target.value);
              if (Number.isNaN(raw)) return;
              onChange(clamp(raw));
            }}
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className={clsx(
              'h-9 appearance-none !bg-buttons/base rounded-radius/button !border-gray-400 px-9 text-center text-text/primary alpha-form-input [appearance:textfield] hover:!border-border/form-hover hover:shadow-md focus:!border md:h-10 md:px-10 lg:h-11 lg:px-10 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
              inputClassName,
            )}
            labelProps={{
              className: 'before:content-none after:content-none',
            }}
            containerProps={{
              className: '!min-w-0 !h-9 md:!h-10 lg:!h-11',
            }}
          />
        )}
        {hideInput && (
          <div
            className="flex h-9 items-center justify-center rounded-radius/button border border-gray-400 px-9 text-center bg-buttons/base text-text/primary alpha-form-input md:h-10 md:px-10 lg:h-11"
            aria-live="polite"
            // Lets a test wait for the committed count before acting on it
            // (CMS-1878); the live region reflows and steals the next click.
            data-testid={id ? `${id}-value` : undefined}
          >
            {value}
          </div>
        )}
        {
          <>
            <IconButton
              disabled={disabled || value <= minValue}
              size="sm"
              variant="text"
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className={classNames(
                '!absolute left-[10px] top-[6px] rounded-full md:right-3 md:top-2',
                '!md:!size-shapes/icon-size-lg !lg:!size-shapes/icon-size-lg !size-shapes/icon-size-lg',
              )}
              onClick={() => change(-step)}
              aria-label="Decrease"
            >
              <DoNotDisturbOn className="size-shapes/icon-size-lg text-brand-primary" />
            </IconButton>
            <IconButton
              disabled={disabled || (!!maxValue && value >= maxValue)}
              size="sm"
              variant="text"
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="!absolute right-[10px] top-[6px] !size-shapes/icon-size-lg rounded-full md:right-3 md:top-2"
              onClick={() => change(step)}
              aria-label="Increase"
              // `id` reaches the label and the optional input only, so the
              // counter is unaddressable with `hideInput` (CMS-1878).
              data-testid={id ? `${id}-increase` : undefined}
            >
              <AddCircle className="size-shapes/icon-size-lg text-brand-primary" />
            </IconButton>
          </>
        }
      </div>
    </div>
  );
}
